import type { AskSSEResponseImprovementQuestionType } from "@performyard/service-contracts";

/**
 * Write an accepted improvement into the review page's answer field.
 *
 * The review form is the legacy template, not React, so this drives the DOM the way a person would —
 * typing into the field or clicking the option — because review.js hangs its autogrow, dirty flag and
 * answer collector off those events. Returns false when the answer can't be written: a stale
 * suggestion must report failure, not leave a card claiming "Change Applied" over an unchanged answer.
 */
export const applyReviewAnswer = (
	questionIndex: number,
	questionType: AskSSEResponseImprovementQuestionType,
	answer: string | number | string[]
): boolean => {
	if (typeof document === "undefined") return false;

	// data-index is the question's position in the form, which is what the agent addresses answers by.
	const block = document.querySelector<HTMLElement>(`#report .question-block[data-index="${questionIndex}"]`);
	if (!block) return false;

	const applied = writeAnswer(block, questionType, answer);
	if (!applied) return false;

	// Bring the rewritten answer into view so applying from the panel doesn't feel like nothing happened.
	block.scrollIntoView({ behavior: "smooth", block: "center" });
	flashAnswer(block);
	return true;
};

/** Utility from the shared Tailwind theme (PY-13212) — app.css loads on this legacy page too. */
const FLASH_CLASS = "animate-input-flash";

/** One-shot flash over the fields just written, so the change is visible after the scroll lands. The
 *  class comes off at animationend, or a second apply to the same question wouldn't replay it. */
const flashAnswer = (block: HTMLElement): void => {
	const targets = block.querySelectorAll<HTMLElement>("textarea, input.isanumber, .answer-list");
	targets.forEach((target) => {
		target.classList.remove(FLASH_CLASS);
		// Next frame, or the browser coalesces remove+add and never restarts the animation.
		requestAnimationFrame(() => {
			target.classList.add(FLASH_CLASS);
			target.addEventListener("animationend", () => target.classList.remove(FLASH_CLASS), { once: true });
		});
	});
};

const writeAnswer = (
	block: HTMLElement,
	questionType: AskSSEResponseImprovementQuestionType,
	answer: string | number | string[]
): boolean => {
	switch (questionType) {
		case "text":
			return typeof answer === "string" && setInputValue(block.querySelector("textarea"), answer);
		case "numeric":
			return typeof answer === "number" && setInputValue(block.querySelector("input.isanumber"), String(answer));
		case "multiline":
			return Array.isArray(answer) && setMultilineValue(block, answer);
		case "multiple-choice":
		case "rating-scale":
			return setSingleChoice(block, answer);
		case "multiple-select":
			return Array.isArray(answer) && setMultiSelect(block, answer);
		default:
			return false;
	}
};

const setInputValue = (field: Element | null, value: string): boolean => {
	if (!(field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement)) return false;
	if (field.disabled || field.readOnly) return false;

	field.value = value;
	// `input` drives review.js's cut/paste handling, and `change`/`keyup` are what its autogrow binds
	// to. `keydown` is the one that sets its unsaved-changes flag, so it has to be dispatched too or
	// the author can navigate away from an applied answer with no warning.
	field.dispatchEvent(new Event("input", { bubbles: true }));
	field.dispatchEvent(new Event("change", { bubbles: true }));
	field.dispatchEvent(new KeyboardEvent("keydown", { bubbles: true }));
	field.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true }));
	return true;
};

/** Bind the page's autogrow to a row we added ourselves: a cloned node carries no jQuery handlers, so
 *  without this it never resizes, never removes itself when emptied, and enter opens no next row. */
const bindAutogrow = (textarea: HTMLTextAreaElement): void => {
	const jquery = (window as unknown as { jQuery?: (el: Element) => { autogrow?: () => void } }).jQuery;
	try {
		jquery?.(textarea).autogrow?.();
	} catch {
		// Cosmetic only — the row still holds its text and still saves.
	}
};

/** Multiline answers are one textarea per line: reuse the rows that exist, clone the last for extra
 *  lines, blank the leftovers — `collect()` drops a blank row from the saved list on its own. */
const setMultilineValue = (block: HTMLElement, lines: string[]): boolean => {
	const rows = Array.from(block.querySelectorAll<HTMLTextAreaElement>("textarea.multiline"));
	if (rows.length === 0) return false;

	// Each new row goes directly after the one before it, the way review.js inserts its own. Appending
	// to the shared parent instead would drop them past the question's trailing markup (the rephrase
	// box), so the answer would read as split in two.
	let previousRow = rows[rows.length - 1].parentElement;

	lines.forEach((line, index) => {
		if (index < rows.length) {
			setInputValue(rows[index], line);
			return;
		}
		// More lines than rows: clone the row's wrapper so the new textarea keeps the page's markup.
		if (!previousRow) return;
		const clone = previousRow.cloneNode(true) as HTMLElement;
		const cloned = clone.querySelector("textarea");
		previousRow.insertAdjacentElement("afterend", clone);
		previousRow = clone;
		if (cloned instanceof HTMLTextAreaElement) {
			setInputValue(cloned, line);
			bindAutogrow(cloned);
		}
	});

	for (let index = lines.length; index < rows.length; index++) {
		setInputValue(rows[index], "");
	}

	return true;
};

/** The option list's `neo` class is the page's "selected" marker, toggled by review.js on click. */
const findChoice = (block: HTMLElement, answer: string | number): HTMLElement | null => {
	const choices = Array.from(block.querySelectorAll<HTMLElement>(".answer-list .answer-choice"));
	if (choices.length === 0) return null;

	// Rating scales are addressed by position (what the page stores); choice lists by option label.
	if (typeof answer === "number") return choices[answer] ?? null;
	return (
		choices.find((choice) => {
			const option = choice.querySelector<HTMLElement>(".choice-block");
			const label = option?.dataset.choiceValue ?? option?.textContent ?? "";
			return label.trim().toLowerCase() === answer.trim().toLowerCase();
		}) ?? null
	);
};

const clickChoice = (choice: HTMLElement): void => {
	// The handler is delegated from the list and toggles, so click only to change state.
	choice.dispatchEvent(new MouseEvent("click", { bubbles: true }));
};

const setSingleChoice = (block: HTMLElement, answer: string | number): boolean => {
	if (typeof answer !== "string" && typeof answer !== "number") return false;
	const choice = findChoice(block, answer);
	if (!choice) return false;
	// Already selected — the click handler would toggle it back off.
	if (!choice.classList.contains("neo")) clickChoice(choice);
	return true;
};

const setMultiSelect = (block: HTMLElement, answers: string[]): boolean => {
	const targets = answers.map((answer) => findChoice(block, answer));
	// All-or-nothing: half a selection is a wrong answer, not a partial one.
	if (targets.some((target) => target === null)) return false;

	const wanted = new Set(targets as HTMLElement[]);
	const choices = Array.from(block.querySelectorAll<HTMLElement>(".answer-list .answer-choice"));
	for (const choice of choices) {
		const shouldBeSelected = wanted.has(choice);
		if (shouldBeSelected !== choice.classList.contains("neo")) clickChoice(choice);
	}
	return true;
};
